import { Settings } from 'luxon';
import { isObject, isUndefined } from 'lodash';
import { createAction, createSlice } from '@reduxjs/toolkit';
import { extractUserProperties } from '@sda/utils/store';
import { toBCP47Locale } from '@sda/utils/lang';
import { pickPrimitives } from '@sda/utils/type';
import { Reducers } from '@sda/enums/store';
import { getCurrentDefaultZoneName } from '@sda/utils/date';
import type { SignupIntents, ProfileTypes } from '@sda/enums';
import type { Permission } from '@sda/interfaces/permission';
import type { PrivateUser } from '@sda/interfaces/user';
import type { RequireAtLeastOne } from '@sda/helpers/helpers.types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  ExtendedParsedToken, ExtendedUser, FilteredParsedToken, Position,
  SessionState, SigninPayload, EmailSignupPayload,
} from '@sda/interfaces/reducer/session.reducer';
import type { User as AuthUser } from 'firebase/auth';

export const startChangeEmail = createAction<string>(`${Reducers.SESSION}/startChangeEmail`);

/**
 * Signs out the current user
 */
export const startSignout = createAction<string | never>(`${Reducers.SESSION}/startSignout`);
export const startSetSelectedProfile = createAction<{ profileId: string, profileType: ProfileTypes, scope?: string }>(`${Reducers.SESSION}/startSetSelectedProfile`);
export const startEmailLogin = createAction<SigninPayload>(`${Reducers.SESSION}/startEmailLogin`);
export const startAddNameToUser = createAction<{ fullName: string }>(`${Reducers.SESSION}/startAddNameToUser`);
export const startSetSignupIntent = createAction<SignupIntents>(`${Reducers.SESSION}/startSetSignupIntent`);
export const startFetchCurrentProfileOwnerEmail = createAction(`${Reducers.SESSION}/startFetchCurrentProfileOwnerEmail`);
export const startFetchUserLocation = createAction(`${Reducers.SESSION}/startFetchUserLocation`);
export const startEmailSignup = createAction<EmailSignupPayload>(`${Reducers.SESSION}/startEmailSignup`);

/**
 * Log a user in that's been signed in through Firebase Auth
 */
export const startScaffoldingUser = createAction<AuthUser>(`${Reducers.SESSION}/startScaffoldingUser`);
export const startRefetchFirebaseToken = createAction<Permission>(`${Reducers.SESSION}/startRefetchFirebaseToken`);
export const startWatchUser = createAction(`${Reducers.SESSION}/startWatchingUser`);

interface ForgotPasswordPayload {
  readonly id?: string;
  readonly email?: string;
}

/**
 * Sends a forgot password email for the current user, or for the user of the ID specified
 */
export const startSendForgotPasswordEmail = createAction<RequireAtLeastOne<ForgotPasswordPayload, 'id' | 'email'>>(`${Reducers.SESSION}/startSendForgotPasswordEmail`);

/**
 * Updates the user's password and redirects them to the login page when finished
 */
export const startResetUserPassword = createAction<{ password: string, oobCode: string }>(`${Reducers.SESSION}/startResetUserPassword`);

/**
 * Registers the current user for Intercom support
 */
export const startSupportRegistration = createAction(`${Reducers.SESSION}/startSupportRegistration`);

/**
 * Updates the current session to indicate that it accepts cookies or if it's declined
 */
export const startUpdateUserAcceptsCookies = createAction<boolean>(`${Reducers.SESSION}/startUpdateUserAcceptsCookies`);

/**
 * Impersonates the selected user
 * If no userId is passed, it unpersonates the current user
 * Note: Admin-only
 */
export const startToggleImpersonate = createAction<string>(`${Reducers.SESSION}/startToggleImpersonate`);

// User management
export const startUpdateNotification = createAction<PrivateUser['notifications']>(`${Reducers.SESSION}/startUpdateNotification`);
export const startChangePassword = createAction<{ password: string, oldPassword: string }>(`${Reducers.SESSION}/startChangePassword`);
export const startUpdateName = createAction<string>(`${Reducers.SESSION}/startUpdateName`);

export const startToggleMuteUser = createAction<string>(`${Reducers.SESSION}/startToggleMuteUser`);

const initialState: SessionState = {
  acceptsCookies: null,
  anonymousUser: null,
  defaultZoneName: getCurrentDefaultZoneName(),
  fingerprint: null,
  googleAnalyticsClientId: null,
  impersonationPending: false,
  loginPending: false,
  logoutPending: false,
  position: null,
  user: null,
};

const sessionSlice = createSlice({
  initialState,
  name: Reducers.SESSION,
  reducers: {
    setAnonymousUser: (state, action: PayloadAction<ExtendedParsedToken | AuthUser>) => {
      if (!action.payload) {
        state.anonymousUser = null;
      } else {
        let { payload: user } = action;

        if (Object.prototype.hasOwnProperty.call(user, 'acceptsCookies')) {
          const { acceptsCookies } = user as Pick<PrivateUser, 'acceptsCookies'>;

          state.anonymousUser.acceptsCookies = acceptsCookies;
          state.acceptsCookies = acceptsCookies;
        } else if (Object.prototype.hasOwnProperty.call(user, 'locationHash')) {
          const { locationHash } = user as Pick<PrivateUser, 'locationHash'>;

          state.anonymousUser.locationHash = locationHash;
        } else {
          state.anonymousUser = pickPrimitives({ ...state.anonymousUser || {}, ...user });
        }
      }
    },
    setBrowserFingerprint: (state, action: PayloadAction<SessionState['fingerprint']>) => {
      state.fingerprint = action.payload;
    },
    setGoogleAnalyticsClientId: (state, action: PayloadAction<string>) => {
      state.googleAnalyticsClientId = action.payload;
    },
    setCurrentSessionUser: (state, action: PayloadAction<FilteredParsedToken | false>) => {
      state.loginPending = false;

      if (action.payload && isObject(action.payload)) {
        state.user = extractUserProperties(action.payload);
      } else {
        state.user = action.payload as null;
      }
    },
    setImpersonatingStatus: (state, action: PayloadAction<SessionState['impersonationPending']>) => {
      state.impersonationPending = action.payload;
    },
    setLogoutPending: (state, action: PayloadAction<SessionState['logoutPending']>) => {
      state.logoutPending = action.payload;
    },
    updateCurrentSessionUser: (state, action: PayloadAction<ExtendedUser>) => {
      let { payload: user } = action;

      if (user && Object.prototype.hasOwnProperty.call(user, 'locale')) {
        const { locale } = user as PrivateUser;

        Settings.defaultLocale = toBCP47Locale(locale);
      }

      if (!isUndefined(user)
      && user.privateFetched
      && isUndefined(user.acceptsCookies)
      && !isUndefined(state.acceptsCookies)) {
        user = {
          ...user,
          acceptsCookies: state.acceptsCookies,
        };
      }

      state.acceptsCookies = (!isUndefined(user) && !isUndefined(user.acceptsCookies)) ? user.acceptsCookies : state.acceptsCookies;

      state.user = {
        ...state.user || {},
        ...extractUserProperties(user) || {},
      };
    },
    updatePosition: {
      reducer: (state, action: PayloadAction<Position>) => {
        state.position = action.payload || null;
      },
      prepare: (position: Position) => {
        if (!position) {
          return {
            payload: null,
          };
        }

        const { accuracy, latitude, longitude } = position;

        return {
          payload: {
            accuracy,
            latitude,
            longitude,
          },
        };
      },
    },
  },
});

export const {
  setAnonymousUser, setBrowserFingerprint, setCurrentSessionUser, updateCurrentSessionUser,
  setImpersonatingStatus, setLogoutPending, setGoogleAnalyticsClientId, updatePosition,
} = sessionSlice.actions;

export default sessionSlice.reducer;
